import { useTaskStore } from "@features/tasks/store/useTaskStore";
import { useSupportUnreadContext } from "@features/support/context/SupportUnreadContext";
import { useUnreadCount } from "@shared/hooks/useUnreadCount";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useProfile } from "../../shared/hooks/useProfile";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarItem } from "./sidebar/SidebarItem";
import { SidebarSection } from "./sidebar/SidebarSection";
import { sponsors } from "./sidebar/sponsors";
import { SidebarItemType } from "./sidebar/types";
import { useSidebarItems } from "./sidebar/useSidebarItems";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  variant?: "default" | "v2";
}

function Sidebar({ sidebarOpen, setSidebarOpen, variant = "default" }: SidebarProps) {
  const { user } = useProfile();
  const trigger = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLDivElement>(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  const pendingTasksCount = useTaskStore((state) => state.getPendingTasksCount());
  const unreadCount = useUnreadCount();
  const { unreadCount: supportUnreadCount } = useSupportUnreadContext();
  const sidebarItems = useSidebarItems({ userRole: user?.role });

  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      if (!sidebarOpen || event.keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded ? "true" : "false");
    if (sidebarExpanded) {
      document.querySelector("body")?.classList.add("sidebar-expanded");
    } else {
      document.querySelector("body")?.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  const getBadgeForItem = (itemId: string): React.ReactNode => {
    if (itemId === "tasks" && pendingTasksCount > 0) {
      return (
        <div className="flex flex-shrink-0 ml-2">
          <span className={`inline-flex items-center justify-center h-5 text-xs font-medium ${theme.bg.badge} ${theme.text.white} px-2 rounded`}>
            {pendingTasksCount}
          </span>
        </div>
      );
    }
    if (itemId === "messages" && unreadCount > 0) {
      return (
        <div className="flex flex-shrink-0 ml-2">
          <span className={`inline-flex items-center justify-center h-5 text-xs font-medium ${theme.bg.badge} ${theme.text.white} px-2 rounded`}>
            {unreadCount}
          </span>
        </div>
      );
    }
    if (itemId === "support-dashboard" && supportUnreadCount > 0) {
      return (
        <div className="flex-shrink-0 ml-2">
          <div className="inline-flex items-center justify-center h-5 w-5 text-xs font-medium text-white bg-red-500 rounded-full">
            {supportUnreadCount > 9 ? "9+" : supportUnreadCount}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-w-fit">
      <div
        className={`fixed inset-0 bg-gray-900 bg-opacity-30 z-40 md:hidden md:z-auto transition-opacity duration-200 ${sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        aria-hidden="true"
      />

      <div
        id="sidebar"
        ref={sidebar}
        className={`flex md:!flex flex-col absolute z-40 left-0 top-0 md:static md:left-auto md:top-auto md:translate-x-0 h-[100dvh] overflow-y-scroll md:overflow-y-auto no-scrollbar w-64 md:w-20 md:sidebar-expanded:!w-64 xl:!w-64 shrink-0 bg-white dark:bg-gray-800 p-4 transition-all duration-200 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-64"
          } ${variant === "v2"
            ? "border-r border-gray-200 dark:border-gray-700/60"
            : "rounded-r-2xl shadow-sm"
          }`}
      >
        <div className="flex justify-between mb-10">
          <button
            ref={trigger}
            className={`md:hidden ${theme.text.muted}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          <NavLink end to="/" className="block">
            <img
              src="/assets/images/logo.png"
              alt="GCCA Logo"
              className="h-14 object-contain dark:hidden md:sidebar-expanded:block xl:block md:hidden"
            />
            <img
              src="/assets/images/logo-white.png"
              alt="GCCA Logo"
              className="h-14 object-contain hidden dark:block md:sidebar-expanded:dark:block xl:dark:block md:dark:hidden"
            />
            <img
              src="/assets/images/logo-secondary.png"
              alt="GCCA Logo"
              className="h-12 object-contain hidden md:block md:sidebar-expanded:hidden xl:hidden dark:hidden"
            />
            <img
              src="/assets/images/logo-secondary-white.png"
              alt="GCCA Logo"
              className="h-12 object-contain hidden dark:md:block dark:md:sidebar-expanded:hidden dark:xl:hidden"
            />
          </NavLink>
        </div>

        <div className="space-y-2 flex-1 overflow-y-auto no-scrollbar">
          <ul>
            {sidebarItems.map((item) => {
              if (item.type === SidebarItemType.SECTION) {
                return (
                  <SidebarSection
                    key={item.id}
                    item={item}
                    onExpand={() => setSidebarExpanded(true)}
                    getBadgeForItem={getBadgeForItem}
                    sidebarExpanded={sidebarExpanded}
                  />
                );
              }
              if (item.type === SidebarItemType.LINK) {
                return (
                  <SidebarItem
                    key={item.id}
                    item={item}
                    badge={getBadgeForItem(item.id)}
                  />
                );
              }
              if (item.type === SidebarItemType.GROUP) {
                return (
                  <SidebarGroup
                    key={item.id}
                    item={item}
                    onExpand={() => setSidebarExpanded(true)}
                  />
                );
              }
              return null;
            })}
          </ul>
        </div>

        <div className="mt-4 md:opacity-0 md:sidebar-expanded:opacity-100 xl:opacity-100 transition-opacity duration-200">
          <h3 className="font-madefor text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold pl-3 mb-3">Our sponsors</h3>
          <div className="flex flex-col gap-3 px-3">
            {sponsors.map((sponsor) => (
              <a
                key={sponsor.name}
                href={sponsor.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:opacity-80 transition-opacity"
              >
                <img
                  src={sponsor.logoUrl}
                  alt={sponsor.name}
                  className="h-8 w-auto object-contain"
                />
              </a>
            ))}
          </div>
        </div>

        <div className="pt-3 hidden md:inline-flex xl:hidden justify-end mt-auto">
          <div className="w-12 pl-4 pr-3 py-2">
            <button
              className={`text-gray-400 hover:${theme.text.muted} dark:${theme.text.muted}`}
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
            >
              <span className="sr-only">Expand / collapse sidebar</span>
              <svg
                className={`shrink-0 fill-current ${theme.text.inactive} sidebar-expanded:rotate-180`}
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d="M15 16a1 1 0 0 1-1-1V1a1 1 0 1 1 2 0v14a1 1 0 0 1-1 1ZM8.586 7H1a1 1 0 1 0 0 2h7.586l-2.793 2.793a1 1 0 1 0 1.414 1.414l4.5-4.5A.997.997 0 0 0 12 8.01M11.924 7.617a.997.997 0 0 0-.217-.324l-4.5-4.5a1 1 0 0 0-1.414 1.414L8.586 7M12 7.99a.996.996 0 0 0-.076-.373Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
